import React, { HTMLAttributes, useEffect, useState } from 'react'
import flipSound from '../assets/sounds/flip.mp3'
import { menus } from '@/data/menu'
import { iconComponents } from '@/data/icon'
import useModalStore from '@/store/modal.store'
import { responsiveSize } from '@/data/responsive-size'
import CProfileImage from '@/assets/images/cp1.png'
import CProfileImage2 from '@/assets/images/cp2.png'

type Props = HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode
}

const GridBackground = ({ children, ...props }: Props) => {
  const [profileSwap, setProfileSwap] = useState<string>(CProfileImage)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set())
  const [flippedIcons, setFlippedIcons] = useState<Set<number>>(new Set())
  const { onOpen } = useModalStore()

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  const baseCellSize = 75
  const minCellSize = 40
  const maxCellSize = 100
  const responsiveCellSize = Math.max(minCellSize, Math.min(maxCellSize, dimensions.width / 20))

  const cellSize = responsiveCellSize
  const cols = Math.ceil(dimensions.width / cellSize)
  const rows = Math.ceil(dimensions.height / cellSize)
  const totalCells = cols * rows

  const centerCol = Math.floor(cols / 2)
  const centerRow = Math.floor(rows / 2)
  const profileIndex = ((centerRow - 1) * cols + centerCol) - (responsiveSize.includes(dimensions.width) ? 1 : 0)


  const surroundingIndices = [
    profileIndex - cols - 1,
    profileIndex - cols,
    profileIndex - cols + 1,
    profileIndex - 1,
    profileIndex + 1,
    profileIndex + cols - 1,
    profileIndex + cols,
    profileIndex + cols + 1,
    profileIndex + cols * 2 - 1,
    profileIndex + cols * 2,
  ]

  const iconSurroundingIndices = [
    // Second ring (starting beyond reserved first ring)
    profileIndex - cols * 3 - 2, // two rows up, two columns left
    profileIndex - cols * 3 - 1, // two rows up, one column left
    profileIndex - cols * 2,     // two rows up, same column
    profileIndex - cols * 3 + 1, // two rows up, one column right
    profileIndex - cols * 3 + 2, // two rows up, two columns right
    profileIndex - cols * 2 - 3,     // one row up, three columns left
    profileIndex - cols * 2 + 3,     // one row up, three columns right

    profileIndex - cols - 4, // three rows up, same column
    profileIndex - cols + 4,
    profileIndex - 4,            // same row, three columns left
    profileIndex + 4,            // same row, three columns right

    profileIndex + cols - 3,     // one row down, three columns left
    profileIndex + cols + 3,     // one row down, three columns right

    profileIndex + cols * 2 - 2, // two rows down, two columns left
    profileIndex + cols * 3 - 1, // two rows down, one column left
    profileIndex + cols * 3,     // two rows down, same column
    profileIndex + cols * 3 + 1, // two rows down, one column right
    profileIndex + cols * 2 + 2, // two rows down, two columns right

    profileIndex + cols * 4,     // three rows down, same column
  ];

  const toggleMenuFlip = (index: number) => {
    new Audio(flipSound).play()
    if (flippedCards.has(surroundingIndices[0])) {
      setFlippedCards(new Set())
      if (flippedIcons.has(iconSurroundingIndices[0])) {
        setFlippedIcons(new Set())
      }
    } else {
      setFlippedCards(new Set(surroundingIndices))
    }
  }

  const toggleIconFlip = (index: number) => {
    new Audio(flipSound).play()
    if (flippedIcons.has(iconSurroundingIndices[0])) {
      setFlippedIcons(new Set())
    } else {
      setFlippedIcons(new Set(iconSurroundingIndices))
    }
  }

  return (
    <div className="relative h-screen w-screen bg-black overflow-hidden" {...props}>
      <div
        className="absolute inset-0 grid z-0"
        style={{
          gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
          gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
        }}
      >
        {Array.from({ length: totalCells }).map((_, index) => {
          const isProfile = index === profileIndex

          const menuIdx = surroundingIndices.indexOf(index)
          const menuItem = menuIdx !== -1 ? menus[menuIdx] : null

          const iconMenuIdx = iconSurroundingIndices.indexOf(index)
          const iconMenuItem = iconMenuIdx !== -1 ? iconComponents[iconMenuIdx] : null

          const isMenuFlipped = flippedCards.has(index)
          const isIconFlipped = flippedIcons.has(index)

          return (
            <div
              id={`cell-${index}`}
              key={index}
              className={`
                hover:bg-white/5 border-r border-b 
                border-blue-400/20 bg-transparent animate-pulse 
                relative flex items-center justify-center
                active:scale-110 active:z-50
                
                ${isProfile ? 'cursor-pointer' : ''}
              `}
              style={{
                animationDelay: `${(index % cols) * 0.1}s`,
                animationDuration: '5s',
              }}
              onClick={() => {
                if (isProfile) {
                  toggleMenuFlip(profileIndex)
                }
                if (menuItem?.content !== "Skills" && menuItem && isMenuFlipped) {
                  onOpen(menuItem?.content as ModalMode)
                }
              }}
            >
              {isProfile && (
                <div
                  onClick={() => setProfileSwap(prev => prev === CProfileImage ? CProfileImage2 : CProfileImage)}
                  className="absolute inset-0 flex items-center justify-center">
                  <img
                    src={profileSwap}
                    alt="profile"
                    className={`w-full h-full object-cover transition-transform duration-500`}
                  />
                </div>
              )}

              {iconMenuItem && (
                <div
                  className={`relative w-full h-full perspective text-white hover:text-pink-700`}
                >
                  {/* Card container with 3D effect */}
                  <div
                    className={`
                      absolute inset-0 flex 
                      p-2
                      backface-hidden items-center 
                      justify-center ${!isIconFlipped ? 'rotate-y-180' : 'cursor-pointer bg-pink-400'}
                      transition-all duration-500 transform-style-preserve-3d
                      `}>
                    {iconMenuItem}
                  </div>
                  <div className="absolute w-full h-full flex items-center justify-center text-white backface-hidden rotate-y-180" />
                </div>
              )}

              {(menuItem) && (
                <div
                  className={`relative w-full h-full perspective text-white hover:text-blue-400`}
                  onClick={() => {
                    if (menuItem.content === 'Skills' && isMenuFlipped) {
                      toggleIconFlip(index)
                    }
                  }}
                >
                  {/* Card container with 3D effect */}
                  <div className={`
                    absolute w-full h-full transition-all duration-500 
                    transform-style-preserve-3d 
                    ${!isMenuFlipped ? 'rotate-y-180 ' : 'cursor-pointer bg-gray-900'}`
                  }>
                    {/* Front side */}
                    <div className="absolute w-full h-full flex flex-col items-center justify-center backface-hidden">
                      <div
                        className="flex flex-col items-center gap-y-1"
                        style={{
                          fontSize: `${cellSize * 0.13}px`, // text size
                        }}
                      >
                        <div className="flex items-center justify-center">
                          {menuItem.icon}
                        </div>
                        <div className="uppercase tracking-wide">
                          {menuItem.content}
                        </div>
                      </div>
                    </div>
                    {/* Back side */}
                    <div className="absolute w-full h-full flex items-center justify-center text-white backface-hidden rotate-y-180" />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {children}
    </div>
  )
}

export default GridBackground
